import { useState } from 'react';
import {
  Accordion as ChakraAccordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Flex,
  Text,
  Tooltip
} from '@chakra-ui/react';
import { faInfoCircle, faPencil } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  Badge,
  Button,
  ButtonText,
  Checkbox,
  useToast
} from '@morressier/alembic';
import { QueryKey, useQueries, useQuery } from '@tanstack/react-query';
import {
  editorialChecklistActiveStates,
  editorialChecklistPassedStates,
  editorialChecklistPhases,
  IEditorialChecklistPhase,
  IEditorialFormDataBase,
  IEditorialFormField,
  IEditorialFormSchema,
  ISubmissionWorkflowState,
  IWorkflowStateMeta,
  workPhaseMap
} from '@publishing-engine/domain/models';
import { ISubmissionNotesListItem } from '@publishing-engine/domain/views';

import {
  fetchSubmissionChecklist,
  fetchSubmissionNotes
} from 'src/api/queries';
import { useUpdateEditorialFormData } from 'src/api/commands';
import { useSubmissionData } from 'src/contexts/SubmissionDataProvider';
import { FieldLabel } from 'src/components/Field';
import ProtectedAction from 'src/components/ProtectedAction';
import { ERRORS } from 'src/constants';
import { getCheckListName } from 'src/utils/string';
import AddNoteModal from 'src/components/SubmissionDetailsSidebar/AddNoteModal';
import ChecklistNotesModal from './ChecklistNotesModal';

// "View Note" is hidden for now while notes are still being collected through
// "Add Note". Flip this to true to re-enable the notes list and its query.
const SHOW_CHECKLIST_NOTES = false;

type PhaseChecklist = {
  phase: IEditorialChecklistPhase;
  editorialFormSchema: IEditorialFormSchema;
  editorialFormData?: IEditorialFormDataBase;
};

const getCheckboxFields = (checklist: PhaseChecklist) =>
  checklist.editorialFormSchema.formFields.filter(
    field => field.component === 'Checkbox'
  );

const getFieldValue = (field: IEditorialFormField, checklist: PhaseChecklist) =>
  Boolean(
    checklist.editorialFormData?.caseData?.[
      field.params.caseDataField.split('.').pop() || field.name
    ]
  );

const getCompletedCount = (checklist: PhaseChecklist) =>
  getCheckboxFields(checklist).filter(field => getFieldValue(field, checklist))
    .length;

/**
 * A checklist phase belongs in the log once the submission has entered it.
 * `editorialFormData` is not a reliable signal: a phase can be completed with
 * nothing checked, leaving the form data empty. The workflow state history is
 * used instead, so passed phases still render their schema as read-only.
 *
 * What counts as proof of a passed phase is phase-specific: pre-production is
 * only shown once the submission moved beyond it, because a reverted decision
 * can send it back to revision assessment and a checklist that was never
 * completed should not surface.
 */
const hasReachedChecklistPhase = (
  phase: IEditorialChecklistPhase,
  isActive: boolean,
  currentState: ISubmissionWorkflowState,
  states: IWorkflowStateMeta[] | undefined
) => {
  if (isActive) return true;

  const visitedStates = new Set([
    currentState,
    ...(states ?? []).map(state => state.name)
  ]);

  return editorialChecklistPassedStates[phase].some(state =>
    visitedStates.has(state)
  );
};

/**
 * Groups the assessment notes fetched from the existing notes API by the
 * checklist item they were created from.
 *
 * The notes API has no checklist-item filter, and notes are not stored with a
 * phase (the note command does not accept one), so a category that is reused
 * by more than one phase resolves to the same notes in each phase.
 */
const buildNotesByCategory = (notes: ISubmissionNotesListItem[]) => {
  const map = new Map<string, ISubmissionNotesListItem[]>();

  notes.forEach(note => {
    if (note.contextualInformation?.context !== 'assessment') return;

    const category = note.contextualInformation.checklistCategory;
    if (!category) return;

    map.set(category, [...(map.get(category) ?? []), note]);
  });

  return map;
};

interface PhaseProps {
  checklist: PhaseChecklist;
  isActive: boolean;
  notesByCategory: Map<string, ISubmissionNotesListItem[]>;
  queryKey: QueryKey;
}

const TaskChecklistPhase = ({
  checklist,
  isActive,
  notesByCategory,
  queryKey
}: PhaseProps) => {
  const toast = useToast();
  const {
    submissionId,
    submissionContextId,
    workflowResponse,
    refetchSubmission
  } = useSubmissionData();
  const { articleWorkflow } = workflowResponse;

  const [noteItem, setNoteItem] = useState<{
    heading: string;
    category: string;
  } | null>(null);
  const [viewNotesItem, setViewNotesItem] = useState<{
    heading: string;
    category: string;
  } | null>(null);
  // Tracks the bulk action only, so single checkbox saves do not make the
  // "Complete Checklist" button show a loading state.
  const [isCompletingChecklist, setIsCompletingChecklist] = useState(false);

  const { phase } = checklist;
  const currentState = workflowResponse.workflowData
    .currentState as ISubmissionWorkflowState;

  // Revision checklists live on the concrete revision state (assessment,
  // invitation, pending, finished), so the permission entity follows the
  // current state while the checklist phase is active.
  const permissionPhase =
    editorialChecklistActiveStates[phase].includes(currentState) &&
    phase === 'revisionAssessment'
      ? currentState
      : phase;

  const updateChecklistMutation = useUpdateEditorialFormData(
    submissionId,
    submissionContextId,
    articleWorkflow.id,
    phase,
    queryKey,
    articleWorkflow.organizationId
  );

  const fields = getCheckboxFields(checklist);
  const completedCount = getCompletedCount(checklist);
  const isCompleteChecklistDisabled =
    fields.length > 0 && completedCount >= fields.length;

  const notifyResult = (
    response: Awaited<ReturnType<typeof updateChecklistMutation.mutateAsync>>,
    successTitle: string
  ) => {
    if (response.isSuccess) {
      if (response.submission.workflowData.isAdminChecklistDone) {
        refetchSubmission();
      }

      return toast({
        variant: 'success',
        title: successTitle,
        isClosable: true
      });
    }

    return toast({
      variant: 'error',
      title: ERRORS.somethingWentWrong,
      isClosable: true
    });
  };

  const updateChecklist = async (
    field: IEditorialFormField,
    value: boolean
  ) => {
    const response = await updateChecklistMutation.mutateAsync({
      [field.name]: value
    });

    notifyResult(
      response,
      `${getCheckListName(field.params.labels?.main || field.name)} ${
        value ? 'checked' : 'unchecked'
      }`
    );
  };

  const updateBulkChecklist = async () => {
    const fieldStatusMap = fields.reduce<Record<string, boolean>>(
      (acc, field) => {
        acc[field.name] = true;
        return acc;
      },
      {}
    );

    setIsCompletingChecklist(true);

    try {
      const response = await updateChecklistMutation.mutateAsync(
        fieldStatusMap
      );

      notifyResult(response, 'Task Checklist Completed');
    } finally {
      setIsCompletingChecklist(false);
    }
  };

  const renderItem = (field: IEditorialFormField) => {
    const { name, params } = field;
    const { labels, validations } = params;
    const heading = labels?.main || name;
    const category = getCheckListName(heading);
    const noteCount = notesByCategory.get(category)?.length ?? 0;

    const checkbox = (
      <Checkbox
        id={`${phase}-${name}`}
        name={name}
        ariaLabel={heading}
        isChecked={getFieldValue(field, checklist)}
        isRequired={validations?.required}
        isDisabled={!isActive}
        onChange={e => updateChecklist(field, e.target.checked)}
      >
        <FieldLabel
          isRequired={validations?.required}
          label={heading}
          subText={labels?.description ?? ''}
        />
      </Checkbox>
    );

    return (
      <Flex key={name} gap={2} flexDirection="column">
        {isActive ? (
          <ProtectedAction
            relation="task_completer"
            entityType="journal_phase"
            entityId={`${submissionContextId}_${permissionPhase}`}
            tooltipLabel="You do not have permission to perform this action"
          >
            {checkbox}
          </ProtectedAction>
        ) : (
          checkbox
        )}

        <Flex gap={4} marginStart={7}>
          {SHOW_CHECKLIST_NOTES && noteCount > 0 && (
            <ButtonText
              alignSelf="flex-start"
              onClick={() => setViewNotesItem({ heading: category, category })}
            >
              View Note ({noteCount})
            </ButtonText>
          )}

          {isActive && (
            <ProtectedAction
              relation="task_completer"
              entityType="journal_phase"
              entityId={`${submissionContextId}_${permissionPhase}`}
              tooltipLabel="You do not have permission to perform this action"
            >
              <ButtonText
                alignSelf="flex-start"
                onClick={() => setNoteItem({ heading: category, category })}
              >
                Add Note
              </ButtonText>
            </ProtectedAction>
          )}
        </Flex>
      </Flex>
    );
  };

  return (
    <>
      <AddNoteModal
        isOpen={!!noteItem}
        onClose={() => setNoteItem(null)}
        onAdd={() => setNoteItem(null)}
        isLoading={false}
        name={noteItem ? `${noteItem.heading} Note` : undefined}
        submissionId={submissionId}
        submissionContextId={submissionContextId}
        checklistCategory={noteItem?.category}
      />

      {SHOW_CHECKLIST_NOTES && (
        <ChecklistNotesModal
          isOpen={!!viewNotesItem}
          onClose={() => setViewNotesItem(null)}
          notes={
            viewNotesItem
              ? notesByCategory.get(viewNotesItem.category) ?? []
              : []
          }
          heading={
            viewNotesItem ? `${viewNotesItem.heading} Notes` : 'Checklist Notes'
          }
        />
      )}

      <Flex flexDirection="column" gap={2}>
        <Text textStyle="textContext">
          Ensure all required steps are completed before advancing in the
          submission process.
        </Text>

        <Flex justifyContent="space-between" alignItems="center">
          <Flex alignItems="center" gap={1}>
            <Text>CHECKLIST</Text>
            <Tooltip
              label="Checklist items are kept for every editorial phase. Completed phases are read-only."
              hasArrow
              placement="top"
            >
              <span>
                <FontAwesomeIcon icon={faInfoCircle} />
              </span>
            </Tooltip>
          </Flex>

          {isActive && (
            <ProtectedAction
              relation="task_completer"
              entityType="journal_phase"
              entityId={`${submissionContextId}_${permissionPhase}`}
              tooltipLabel="You do not have permission to perform this action"
            >
              <Button
                variant="secondary"
                width="50%"
                onClick={updateBulkChecklist}
                isDisabled={isCompleteChecklistDisabled}
                isLoading={isCompletingChecklist}
              >
                Complete Checklist
              </Button>
            </ProtectedAction>
          )}
        </Flex>

        <Flex gap={4} flexDirection="column">
          {fields.map(renderItem)}
        </Flex>
      </Flex>
    </>
  );
};

/**
 * Task Checklist Log
 *
 * Renders the checklist for every editorial phase (initial assessment,
 * revision assessment and pre-production) with the current phase editable and
 * completed phases kept as a read-only historical record.
 *
 * The data comes from the existing single-phase checklist endpoint, called
 * once per phase. Only phases with a currently enabled checklist schema are
 * shown, because that is all the existing endpoint exposes.
 */
const TaskChecklistLog = () => {
  const { submissionId, submissionContextId, workflowResponse } =
    useSubmissionData();
  const { articleWorkflow, workflowData } = workflowResponse;

  const phaseQueries = useQueries({
    queries: editorialChecklistPhases.map(phase => ({
      queryKey: [
        'submission-checklist',
        { submissionId, phase, articleWorkflowId: articleWorkflow.id }
      ],
      queryFn: () =>
        fetchSubmissionChecklist(
          submissionId,
          submissionContextId,
          articleWorkflow.organizationId,
          phase,
          articleWorkflow.id
        ),
      enabled: !!submissionId && !!submissionContextId
    }))
  });

  const { data: notesData } = useQuery({
    queryKey: ['submission-notes', { submissionId, checklist: true }],
    queryFn: () =>
      fetchSubmissionNotes({
        ctxId: submissionContextId,
        submissionId,
        limit: 1000,
        category: ['Assessment']
      }),
    enabled: SHOW_CHECKLIST_NOTES && !!submissionId && !!submissionContextId
  });

  const currentState = workflowData.currentState as ISubmissionWorkflowState;

  const checklists = editorialChecklistPhases.flatMap<PhaseChecklist>(
    (phase, index) => {
      const query = phaseQueries[index];
      if (!query?.data?.editorialFormSchema) return [];

      // Show the active phase and every phase the submission already moved
      // through, so a passed phase with no saved form data still renders its
      // schema as read-only. Phases never reached stay out of the log.
      const isActive =
        editorialChecklistActiveStates[phase]?.includes(currentState) ?? false;
      if (
        !hasReachedChecklistPhase(
          phase,
          isActive,
          currentState,
          workflowData.states
        )
      ) {
        return [];
      }

      return [
        {
          phase,
          editorialFormSchema: query.data.editorialFormSchema,
          editorialFormData: query.data.editorialFormData
        }
      ];
    }
  );

  if (checklists.length === 0) {
    return null;
  }

  const notesByCategory = buildNotesByCategory(notesData?.items ?? []);

  return (
    <Flex
      backgroundColor="surfacePrimaryFill"
      width="100%"
      padding={4}
      flexDirection="column"
    >
      <ChakraAccordion allowToggle defaultIndex={0} width="100%">
        <AccordionItem
          border="1px solid"
          borderColor="neutralPrimaryBorder"
          borderRadius="4px"
          overflow="hidden"
        >
          <AccordionButton py={4} px={3} bgColor="surfaceSecondaryFill">
            <Flex
              width="100%"
              alignItems="flex-start"
              justifyContent="space-between"
            >
              <Flex alignItems="flex-start" gap={2}>
                <Badge size="context" variant="interactive">
                  <FontAwesomeIcon icon={faPencil} />
                </Badge>
                <Flex flexDirection="column" marginLeft={2}>
                  <Text textStyle="headingItem">Task Checklist Log</Text>
                  {/* <Text textStyle="textContext" textAlign="left">
                    Review the checklist for every editorial phase of this
                    submission.
                  </Text> */}
                </Flex>
              </Flex>
              <AccordionIcon fontSize="sm" flexShrink={0} />
            </Flex>
          </AccordionButton>

          <AccordionPanel p={0} background="surfacePrimaryFill">
            <ChakraAccordion
              allowMultiple
              defaultIndex={checklists.map((_, index) => index)}
              width="100%"
            >
              {checklists.map(checklist => {
                const { phase } = checklist;
                const isActive =
                  editorialChecklistActiveStates[phase]?.includes(
                    currentState
                  ) ?? false;
                const completedCount = getCompletedCount(checklist);
                const totalCount = getCheckboxFields(checklist).length;

                return (
                  <AccordionItem
                    key={phase}
                    border="none"
                    borderTop="1px solid"
                    borderColor="neutralPrimaryBorder"
                  >
                    <AccordionButton px={3} py={2}>
                      <Flex
                        width="100%"
                        alignItems="center"
                        justifyContent="space-between"
                        gap={2}
                      >
                        <Flex alignItems="center" gap={2}>
                          <Text textStyle="headingItem">
                            {workPhaseMap[phase]} Task Checklist
                          </Text>
                          <Text textStyle="textContext">
                            ({completedCount}/{totalCount})
                          </Text>
                        </Flex>
                        <AccordionIcon fontSize="sm" />
                      </Flex>
                    </AccordionButton>

                    <AccordionPanel px={3} pt={2} pb={4}>
                      <TaskChecklistPhase
                        checklist={checklist}
                        isActive={isActive}
                        notesByCategory={notesByCategory}
                        queryKey={[
                          'submission-checklist',
                          {
                            submissionId,
                            phase,
                            articleWorkflowId: articleWorkflow.id
                          }
                        ]}
                      />
                    </AccordionPanel>
                  </AccordionItem>
                );
              })}
            </ChakraAccordion>
          </AccordionPanel>
        </AccordionItem>
      </ChakraAccordion>
    </Flex>
  );
};

export default TaskChecklistLog;
